import { supabase } from "@/integrations/supabase/client";
import { getFinancialYearSuffix } from "@/utils/financialYear";

const DOCNUM_CACHE_PREFIX = "docnum_cache_";

const SERIES_DOC_TYPE_MAP: Record<string, string> = {
  invoice:           "sale_invoice",
  return:            "sale_return",
  order:             "sale_order",
  challan:           "sale_challan",
  retail:            "sale_retail",
  "service-general": "sale_service_general",
  "service-rental":  "sale_service_rental",
  "service-jobwork": "sale_service_jobwork",
  purchase_bill:     "purchase_bill",
  purchase_order:    "purchase_order",
  purchase_return:   "purchase_return",
  purchase_challan:  "purchase_challan",
  jobwork_out:       "jobwork_out",
  jobwork_in:        "jobwork_in",
};

function cacheKey(table: string, prefix: string): string {
  return `${DOCNUM_CACHE_PREFIX}${table}_${prefix}`;
}

function readCache(table: string, prefix: string): number {
  try {
    const val = localStorage.getItem(cacheKey(table, prefix));
    return val ? parseInt(val, 10) : 0;
  } catch { return 0; }
}

function writeCache(table: string, prefix: string, num: number): void {
  try {
    localStorage.setItem(cacheKey(table, prefix), String(num));
  } catch { /* storage quota */ }
}

function cacheDocNumber(table: string, prefix: string, docNumber: string): void {
  if (!docNumber.startsWith(prefix)) return;
  const middle = docNumber.slice(prefix.length).replace(/-\d{4}$/, '');
  const num = parseInt(middle, 10);
  if (!isNaN(num)) writeCache(table, prefix, num);
}

function getCurrentFYRange(): { start: string; end: string } {
  const now = new Date();
  const month = now.getMonth();
  const year = now.getFullYear();
  const startYear = month < 3 ? year - 1 : year;
  return { start: `${startYear}-04-01`, end: `${startYear + 1}-03-31` };
}

async function getCurrentTenantIdForDocNumber(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data: profile } = await supabase
    .from("profiles")
    .select("tenant_id")
    .eq("id", user.id)
    .maybeSingle();
  return profile?.tenant_id || null;
}

async function scanSequentialDocNumber(
  table: "sale_documents" | "purchase_documents" | "jobwork_documents",
  prefix: string,
  docTypeColumn?: string,
  docTypeValue?: string,
  resetByFY?: boolean,
  tenantId?: string | null,
): Promise<string> {
  const fySuffix = resetByFY ? getFinancialYearSuffix() : "";
  const fyRange = resetByFY ? getCurrentFYRange() : null;
  let query = (supabase.from(table) as any).select("doc_number,doc_date").like("doc_number", `${prefix}%`);
  if (tenantId) query = query.eq("tenant_id", tenantId);
  if (docTypeColumn && docTypeValue) query = query.eq(docTypeColumn, docTypeValue);

  const { data: rows, error } = await query;
  if (error) throw error;

  let maxNumber = 0;
  let fyMaxNumber = 0;
  for (const row of rows || []) {
    const docNum = row.doc_number as string;
    if (!docNum || docNum.includes("(offline)") || !docNum.startsWith(prefix)) continue;
    const stripped = docNum.slice(prefix.length).replace(/-\d{4}$/, '');
    const num = parseInt(stripped, 10);
    if (isNaN(num)) continue;
    if (num > maxNumber) maxNumber = num;

    const rowDate = row.doc_date || null;
    if (resetByFY && fyRange && rowDate && rowDate >= fyRange.start && rowDate <= fyRange.end && num > fyMaxNumber) {
      fyMaxNumber = num;
    }
  }

  const startNumber = resetByFY ? fyMaxNumber + 1 : maxNumber + 1;
  return `${prefix}${startNumber}${fySuffix}`;
}

async function generateAtomicDocNumber(
  table: "sale_documents" | "purchase_documents" | "jobwork_documents",
  prefix: string,
  docTypeColumn?: string,
  docTypeValue?: string,
  resetByFY?: boolean,
): Promise<string | null> {
  const tenantId = await getCurrentTenantIdForDocNumber();
  const { data, error } = await supabase.rpc("generate_doc_number_atomic", {
    p_table: table,
    p_prefix: prefix,
    p_doc_type_col: docTypeColumn || null,
    p_doc_type_val: docTypeValue || null,
    p_reset_by_fy: resetByFY || false,
    p_tenant_id: tenantId || null,
  });
  if (error) throw error;
  return typeof data === "string" && data.trim() ? data : null;
}

export async function generateSequentialDocNumber(
  table: "sale_documents" | "purchase_documents" | "jobwork_documents",
  prefix: string,
  docTypeColumn?: string,
  docTypeValue?: string,
  resetByFY?: boolean,
): Promise<string> {
  const fySuffix = resetByFY ? getFinancialYearSuffix() : "";

  // Offline fallback — stays on frontend
  if (!navigator.onLine) {
    const cached = readCache(table, prefix);
    const next = cached + 1;
    writeCache(table, prefix, next);
    return `${prefix}${next}${fySuffix} (offline)`;
  }

  try {
    const docNumber = await generateAtomicDocNumber(table, prefix, docTypeColumn, docTypeValue, resetByFY);
    if (docNumber) {
      cacheDocNumber(table, prefix, docNumber);
      return docNumber;
    }

    const tenantId = await getCurrentTenantIdForDocNumber();
    const scannedDocNumber = await scanSequentialDocNumber(table, prefix, docTypeColumn, docTypeValue, resetByFY, tenantId);
    cacheDocNumber(table, prefix, scannedDocNumber);
    return scannedDocNumber;
  } catch (err) {
    console.error("Error generating doc number:", err);
    const cached = readCache(table, prefix);
    return `${prefix}${cached + 1}${fySuffix}`;
  }
}

async function generateSalesDocNumberFromSeries(
  prefix: string,
  docType: string,
  companyId?: string,
  docDate?: string,
): Promise<string | null> {
  if (!companyId) return null;
  try {
    const seriesKey = SERIES_DOC_TYPE_MAP[docType] ?? docType;
    const { data, error } = await supabase.rpc("generate_doc_number_from_series", {
      p_company_id: companyId,
      p_doc_type: seriesKey,
      p_doc_date: docDate || new Date().toISOString().slice(0, 10),
    });
    if (error) throw error;
    return typeof data === "string" && data.trim() ? data : null;
  } catch (err) {
    console.warn("[DocNumber] series doc number generation failed, falling back", err);
    return null;
  }
}

export async function generateSaleDocNumberWithFallback(
  prefix: string,
  docType: string,
  resetByFY?: boolean,
  companyId?: string,
  docDate?: string,
): Promise<string> {
  const seriesDocNumber = await generateSalesDocNumberFromSeries(prefix, docType, companyId, docDate);
  if (seriesDocNumber) {
    cacheDocNumber("sale_documents", prefix, seriesDocNumber);
    return seriesDocNumber;
  }

  try {
    const atomicDocNumber = await generateAtomicDocNumber("sale_documents", prefix, "doc_type", docType, resetByFY);
    if (atomicDocNumber) {
      cacheDocNumber("sale_documents", prefix, atomicDocNumber);
      return atomicDocNumber;
    }

    const tenantId = await getCurrentTenantIdForDocNumber();
    const scannedDocNumber = await scanSequentialDocNumber("sale_documents", prefix, "doc_type", docType, resetByFY, tenantId);
    cacheDocNumber("sale_documents", prefix, scannedDocNumber);
    return scannedDocNumber;
  } catch (err) {
    console.warn("[DocNumber] sale doc number generation failed, falling back", err);
  }

  return generateSequentialDocNumber("sale_documents", prefix, "doc_type", docType, resetByFY);
}

